import type { NavItemWithModule } from '../../modules/useNavItems.js';
import { resolveActiveNavPath } from '../nav-active.js';

/** A sidebar group: one collapsible entry whose pages are its children. */
export interface NavGroupNode {
  label: string;
  /** Icon of the group's first item — groups have no icon of their own in the manifests. */
  icon?: string;
  items: NavItemWithModule[];
}

export interface NavTree {
  /** Items without a `group`: rendered as direct links above the groups. */
  topItems: NavItemWithModule[];
  /** Named groups in the order their first item was declared. */
  groups: NavGroupNode[];
}

const byOrder = (a: NavItemWithModule, b: NavItemWithModule) =>
  (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER);

/**
 * Builds the sidebar tree from every enabled module's nav items. Items whose policy the person
 * does not hold are dropped (the route guard still enforces it; this only hides the link).
 * `Array.prototype.sort` is stable, so declaration order breaks `order` ties.
 */
export function buildNavTree(items: NavItemWithModule[], canSee: (policy: string) => boolean): NavTree {
  const visible = items.filter((item) => !item.policy || canSee(item.policy));
  const topItems = visible.filter((item) => !item.group).sort(byOrder);

  const groups: NavGroupNode[] = [];
  const byLabel = new Map<string, NavGroupNode>();
  for (const item of visible) {
    if (!item.group) continue;
    let group = byLabel.get(item.group);
    if (!group) {
      group = { label: item.group, items: [] };
      byLabel.set(item.group, group);
      groups.push(group);
    }
    group.items.push(item);
  }
  for (const group of groups) {
    group.items.sort(byOrder);
    group.icon = group.items[0]?.icon;
  }

  return { topItems, groups };
}

/** Every path in the tree, for the longest-match active rule. */
export function navTreePaths(tree: NavTree): string[] {
  return [...tree.topItems, ...tree.groups.flatMap((group) => group.items)].map((item) => item.path);
}

/** The one active path (longest match) and the group that holds it, if any. */
export function resolveActiveNav(tree: NavTree, pathname: string): { path: string | null; group: string | null } {
  const path = resolveActiveNavPath(pathname, navTreePaths(tree));
  const group = path ? (tree.groups.find((g) => g.items.some((item) => item.path === path))?.label ?? null) : null;
  return { path, group };
}
